import type {
  Account,
  AccountsListParams,
  AccountsListResponse,
  CreateAccountDto,
  UpdateAccountDto,
} from '@/types/accounts';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest } from './base.service';

/**
 * Accounts Service
 * Handles all bank account-related API endpoints from /api/v1/accounts/*
 */

export const accountsService = {
  /**
   * Get bank accounts for a specific user (legacy)
   */
  async getBankAccounts(userId: string, organizationId?: string): Promise<Account[]> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const orgQuery = organizationId
      ? `&organization_id=${encodeURIComponent(organizationId)}`
      : '';

    const response = await authenticatedRequest<AccountsListResponse>(
      `/api/v1/accounts?user_id=${userId}${orgQuery}`,
      {},
      token
    );
    return response.data;
  },

  /**
   * List accounts with filters and pagination
   */
  async listAccounts(params: AccountsListParams = {}): Promise<AccountsListResponse> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.orderBy) queryParams.append('orderBy', params.orderBy);
    if (params.orderDirection) queryParams.append('orderDirection', params.orderDirection);
    if (params.type) queryParams.append('type', params.type);
    if (params.status) queryParams.append('status', params.status);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.organization_id) queryParams.append('organization_id', params.organization_id);

    const query = queryParams.toString();
    return authenticatedRequest<AccountsListResponse>(
      `/api/v1/accounts${query ? `?${query}` : ''}`,
      {},
      token
    );
  },

  /**
   * Get a specific account by ID
   */
  async getAccount(id: string): Promise<Account> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<Account>(`/api/v1/accounts/${id}`, {}, token);
  },

  /**
   * Create a new account
   */
  async createAccount(data: CreateAccountDto): Promise<Account> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<Account>(
      '/api/v1/accounts',
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      token
    );
  },

  /**
   * Update an existing account
   */
  async updateAccount(id: string, data: UpdateAccountDto): Promise<Account> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<Account>(
      `/api/v1/accounts/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      token
    );
  },

  /**
   * Delete an account
   */
  async deleteAccount(id: string): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    await authenticatedRequest<void>(
      `/api/v1/accounts/${id}`,
      {
        method: 'DELETE',
      },
      token
    );
  },

  /**
   * Get organization accounts (helper method)
   */
  async getOrganizationAccounts(status: 'active' | 'inactive' = 'active'): Promise<Account[]> {
    const response = await this.listAccounts({
      status,
      limit: 100,
    });
    return response.data;
  },
};
